import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import CustomerDashboard from "@/pages/CustomerDashboard";
import BusinessDashboard from "@/pages/BusinessDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import NewProjectPage from "@/pages/NewProjectPage";
import ProjectDetailPage from "@/pages/ProjectDetailPage";
import BusinessProfilePage from "@/pages/BusinessProfilePage";
import BusinessPublicProfile from "@/pages/BusinessPublicProfile";
import AuthCallback from "@/pages/AuthCallback";
import SelectRolePage from "@/pages/SelectRolePage";
import FAQPage from "@/pages/FAQPage";
import SettingsPage from "@/pages/SettingsPage";
import ServicePage from "@/pages/ServicePage";
import AnalyticsPage from "@/pages/AnalyticsPage";

// Protected route wrapper
const ProtectedRoute = ({ children, allowedTypes }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedTypes && !allowedTypes.includes(user.user_type)) {
    // Redirect to appropriate dashboard
    if (user.user_type === "customer") return <Navigate to="/customer" replace />;
    if (user.user_type === "business") return <Navigate to="/business" replace />;
    if (user.user_type === "admin") return <Navigate to="/admin" replace />;
  }

  return children;
};

// Redirect based on user type
const DashboardRedirect = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user.user_type === "customer") return <Navigate to="/customer" replace />;
  if (user.user_type === "business") return <Navigate to="/business" replace />;
  if (user.user_type === "admin") return <Navigate to="/admin" replace />;

  return <Navigate to="/" replace />;
};

function AppRouter() {
  const location = useLocation();
  // Check URL fragment for session_id synchronously during render (prevents race conditions)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/faq" element={<FAQPage />} />
      <Route path="/services/:slug" element={<ServicePage />} />
      <Route path="/select-role" element={<SelectRolePage />} />

      {/* Dashboard redirect */}
      <Route path="/dashboard" element={<DashboardRedirect />} />
            
            {/* Customer routes */}
            <Route
              path="/customer"
              element={
                <ProtectedRoute allowedTypes={["customer"]}>
                  <CustomerDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/customer/new-project"
              element={
                <ProtectedRoute allowedTypes={["customer"]}>
                  <NewProjectPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/customer/project/:projectId"
              element={
                <ProtectedRoute allowedTypes={["customer"]}>
                  <ProjectDetailPage />
                </ProtectedRoute>
              }
            />
            
            {/* Business routes */}
            <Route
              path="/business"
              element={
                <ProtectedRoute allowedTypes={["business"]}>
                  <BusinessDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/business/analytics"
              element={
                <ProtectedRoute allowedTypes={["business"]}>
                  <AnalyticsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/business/profile"
              element={
                <ProtectedRoute allowedTypes={["business"]}>
                  <BusinessProfilePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/business/project/:projectId"
              element={
                <ProtectedRoute allowedTypes={["business"]}>
                  <ProjectDetailPage />
                </ProtectedRoute>
              }
            />
            
            {/* Admin routes */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute allowedTypes={["admin"]}>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            
            {/* Public business profile - accessible to all authenticated users */}
            <Route
              path="/business/view/:businessId"
              element={
                <ProtectedRoute allowedTypes={["customer", "business", "admin"]}>
                  <BusinessPublicProfile />
                </ProtectedRoute>
              }
            />
            
            {/* Settings - accessible to all authenticated users */}
            <Route
              path="/settings"
              element={
                <ProtectedRoute allowedTypes={["customer", "business", "admin"]}>
                  <SettingsPage />
                </ProtectedRoute>
              }
            />
            
            {/* Catch all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <div className="App">
        <Toaster position="top-right" richColors />
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
      </div>
    </AuthProvider>
  );
}

export default App;
